import { useCallback, useRef, useState } from "react";

/**
 * Scroll-reveal: vraća [ref, visible], visible postaje true kad element
 * uđe u viewport. Callback ref, pa radi i za uslovno renderovane elemente
 * koji se montiraju tek kad stignu podaci.
 *
 * Observer ostaje aktivan: ako je element ISPOD viewporta a nije vidljiv
 * (npr. učitani podaci su ga gurnuli naniže posle inicijalnog layouta),
 * visible se resetuje na false, animacija se pušta tek kad korisnik
 * stvarno doskroluje do sekcije, ne sve odjednom na load.
 * Pri skrolu nagore nema resetovanja (element iznad viewporta ostaje vidljiv).
 *
 * Uz prefers-reduced-motion element je odmah vidljiv, bez animacije.
 */
const useReveal = ({ rootMargin = "0px 0px -60px 0px", threshold = 0 } = {}) => {
  const [visible, setVisible] = useState(false);
  const observerRef = useRef(null);

  const ref = useCallback(
    (node) => {
      if (observerRef.current) {
        observerRef.current.disconnect();
        observerRef.current = null;
      }
      if (!node) return;

      if (
        typeof IntersectionObserver === "undefined" ||
        window.matchMedia("(prefers-reduced-motion: reduce)").matches
      ) {
        setVisible(true);
        return;
      }

      const observer = new IntersectionObserver(
        ([entry]) => {
          if (entry.isIntersecting) {
            setVisible(true);
          } else if (entry.boundingClientRect.top > 0) {
            // element je ispod viewporta, još nismo stigli do njega
            setVisible(false);
          }
        },
        { rootMargin, threshold },
      );
      observer.observe(node);
      observerRef.current = observer;
    },
    [rootMargin, threshold],
  );

  return [ref, visible];
};

export default useReveal;
